'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var index = require('../../providers/ClaimBuilder/index.js');
var groupPackagesForUI = require('./groupPackagesForUI.js');
var useAnalytics = require('../../providers/useAnalytics.js');
var SessionContext = require('../../providers/SessionContext.js');
var useTitanAnalytics = require('../../providers/titan/useTitanAnalytics.js');
var getItemSelectionTitle = require('./getItemSelectionTitle.js');
var nthHookTranslations = require('@narvar/nth-hook-translations');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

const ItemSelectionContext = /*#__PURE__*/React__default["default"].createContext(null);
const useItemSelection = () => {
  const context = React.useContext(ItemSelectionContext);
  if (!context) {
    throw new Error('No context provided for ItemSelection provider');
  }
  return context;
};
const ItemSelectionProviderBase = _ref => {
  let {
    children,
    value: {
      shipments,
      itemClaims
    }
  } = _ref;
  const {
    state: {
      claimType,
      claimEligibilityInfo
    },
    getFullItemDetails,
    handleConfirmRemoveClaim,
    handleItemClaimSelection
  } = index.useClaimBuilder();
  const logEvent = useAnalytics["default"]();
  const titan = useTitanAnalytics["default"]();
  const {
    orderNumber
  } = SessionContext.useSession();
  const {
    claimsSelectionTitleLine2Item,
    claimsSelectionTitleLine2ItemItemsSelected,
    claimsSelectionTitleLine2ItemNoEligibility,
    claimsSelectionTitleItem,
    claimsSelectionTitleItemItemsSelected,
    claimsSelectionTitleItemNoEligibility
  } = nthHookTranslations.useTranslations();
  const {
    items
  } = claimEligibilityInfo ?? {};
  const {
    title,
    subtitle
  } = getItemSelectionTitle["default"]({
    items,
    itemClaims,
    translations: {
      claimsSelectionTitleLine2Item,
      claimsSelectionTitleLine2ItemItemsSelected,
      claimsSelectionTitleLine2ItemNoEligibility,
      claimsSelectionTitleItem,
      claimsSelectionTitleItemItemsSelected,
      claimsSelectionTitleItemNoEligibility
    }
  });
  const {
    groupedEligibleShipments,
    groupedIneligibleShipments
  } = groupPackagesForUI.groupPackagesForUI(shipments);
  const isSelected = (item, packageId) => itemClaims[`${item.itemRefId}_${packageId}`] !== undefined;
  const sortByItemSelected = packageId => (a, b) => {
    if (isSelected(a, packageId) && !isSelected(b, packageId)) return -1;
    if (isSelected(b, packageId) && !isSelected(a, packageId)) return 1;
    return 0;
  };
  groupedEligibleShipments.forEach(shipment => shipment.items.sort(sortByItemSelected(shipment.id)));
  groupedIneligibleShipments.forEach(shipment => shipment.items.sort(sortByItemSelected(shipment.id)));
  const sortByContainingSelectedItems = shipments => {
    const checkShipmentContainsSelectedItem = shipment => {
      const isItemSelected = item => {
        const claimDetails = itemClaims[`${item.itemRefId}_${shipment.id}`];
        return Boolean(claimDetails);
      };
      return shipment.items.some(item => isItemSelected(item));
    };
    const sortByContainingSelectedItem = (a, b) => {
      if (checkShipmentContainsSelectedItem(a) && !checkShipmentContainsSelectedItem(b)) return -1;
      if (checkShipmentContainsSelectedItem(b) && !checkShipmentContainsSelectedItem(a)) return 1;
      return 0;
    };
    return [...shipments].sort(sortByContainingSelectedItem);
  };
  const handleRemove = _ref2 => {
    let {
      itemUid,
      packageId
    } = _ref2;
    logEvent({
      event_type: useAnalytics.ClaimsEventTypes.REMOVE_ITEM_CLICK,
      event_component: useAnalytics.ClaimsEventComponents.NTH_KIT_CLAIMS_ORDER_LOOKUP,
      order_number: orderNumber,
      claim_reason: claimType,
      page_step: useAnalytics.ClaimsPageStep.SELECTION
    });
    handleConfirmRemoveClaim({
      claimLevel: 'item',
      id: `${itemUid}_${packageId}`
    });
  };
  const handleItemSelection = _ref3 => {
    let {
      itemUid,
      packageId
    } = _ref3;
    const itemDetails = getFullItemDetails(itemUid, packageId);
    if (!itemDetails) throw new Error('Insufficient item details');
    titan === null || titan === void 0 ? void 0 : titan.track({
      event: 'product_clicked',
      properties: {
        product_id: itemDetails.itemId,
        sku: itemDetails.sku,
        name: itemDetails.name
      }
    })
    // eslint-disable-next-line no-console
    .catch(e => console.error(e));
    logEvent({
      event_type: useAnalytics.ClaimsEventTypes.ITEM_SELECTED,
      event_component: useAnalytics.ClaimsEventComponents.NTH_KIT_CLAIMS_ORDER_LOOKUP,
      order_number: orderNumber,
      claim_reason: claimType,
      page_step: useAnalytics.ClaimsPageStep.SELECTION
    });
    handleItemClaimSelection({
      itemUid: itemUid,
      packageId
    });
  };
  return /*#__PURE__*/React__default["default"].createElement(ItemSelectionContext.Provider, {
    value: {
      eligibleShipments: sortByContainingSelectedItems(groupedEligibleShipments),
      ineligibleShipments: sortByContainingSelectedItems(groupedIneligibleShipments),
      title,
      subtitle,
      itemClaims,
      handleRemove,
      handleItemSelection,
      sortByContainingSelectedItems
    }
  }, children);
};
const ItemSelectionProvider = nthHookTranslations.withTranslationsProvider(ItemSelectionProviderBase, {
  translationKeys: ['claimsSelectionTitleLine2Item', 'claimsSelectionTitleLine2ItemItemsSelected', 'claimsSelectionTitleLine2ItemNoEligibility', 'claimsSelectionTitleItem', 'claimsSelectionTitleItemItemsSelected', 'claimsSelectionTitleItemNoEligibility']
});

exports.ItemSelectionContext = ItemSelectionContext;
exports.ItemSelectionProvider = ItemSelectionProvider;
exports.ItemSelectionProviderBase = ItemSelectionProviderBase;
exports.useItemSelection = useItemSelection;
